import { IChatbotNavAction } from "api/chatbot/chatbot.type";
import { isModuleEnabled, ModulePermissions } from "types/permissions";

/**
 * Should this chatbot navigation action be offered to this user?
 *
 * Pure on purpose: the two permission defaults are NOT interchangeable, and inverting them
 * would either hide working destinations or — worse — offer a button into a feature the
 * user's plan doesn't include. This mirrors `useMenuItems.ts` exactly:
 *
 *   default_visible === false → STRICT. Show only when explicitly granted (`is_visible === true`).
 *                               Used by modules that are off until billing provisions them
 *                               (design, collection_range).
 *   otherwise                 → Show unless explicitly revoked (`is_visible !== false`).
 *
 * Trap #5: a module MISSING from the permissions payload means "not enabled", never
 * "enabled by default" — hence `=== true` rather than `!== false` on the strict path. And a
 * permissions object that hasn't loaded yet (undefined) is not proof of anything, so a strict
 * destination stays hidden until the payload arrives.
 */
export const isNavActionVisible = (action: IChatbotNavAction, permissions?: ModulePermissions): boolean => {
  if (!action?.route || !action?.label) return false;
  if (!action.permission_module) return true;

  const isVisible = permissions?.[action.permission_module as keyof ModulePermissions]?.is_visible;

  return action.permission_default_visible === false ? isVisible === true : isVisible !== false;
};

export const filterNavActions = (actions: IChatbotNavAction[] | undefined, permissions?: ModulePermissions): IChatbotNavAction[] => (actions ?? []).filter((a) => isNavActionVisible(a, permissions));

/**
 * ── Guided workflows (REF-6217 Phase 2) ─────────────────────────────────────────────────────
 *
 * Offering a guided run needs TWO independent answers, kept apart because they fail for unrelated
 * reasons and are checked against different sources:
 *
 *   `isWorkflowSupported(action, definition)` — does THIS BUILD know the flow the backend named?
 *   `isGuidedActionEligible(action, access)`  — can THIS USER actually enter it?
 *
 * A renderer needs both: `isWorkflowSupported(a, findGuidedFlow(a.workflow_id ?? "")) && isGuidedActionEligible(a, access)`.
 *
 * Both are pure and both stay generic over the metadata the action already carries. There is no
 * capability id, workflow id or permission name written down here — Tech Pack's identity lives in
 * its own authored definition and in the generated graph, and this module must keep working
 * unchanged when a second flow appears.
 */

/** The shape of a flow definition these predicates need — passed in, so this module imports none. */
interface WorkflowIdentity {
  id: string;
  version: number;
  capabilityId: string;
}

/** What the app knows about the current user's access. All of it already lives in `common`. */
export interface GuidedAccessState {
  permissions?: ModulePermissions;
  packageCode: string;
  /**
   * `common.isAuthorized`. Named for what it *is* — a bootstrapped session — because that is what
   * `useBootstrapSession` sets it on, and it is NOT evidence of a paid plan. It matters here only
   * because it is the flag `AuthorizationPage` gates `paidPlanNeeded` routes on.
   */
  hasSession: boolean;
}

/**
 * Does the backend's workflow metadata name a flow this build can actually run?
 *
 * The definition is a parameter rather than an import so this stays a pure predicate with no path
 * into the guidance runtime or a feature's page module. Version equality, not comparison: Phase 2
 * has no migration story, so a definition that has moved on simply stops being offered.
 */
export const isWorkflowSupported = (action: IChatbotNavAction, definition: WorkflowIdentity | null | undefined): boolean => {
  if (!action?.workflow_id || !definition) return false;

  return (
    definition.id === action.workflow_id &&
    definition.version === action.workflow_version &&
    // Generation already guarantees this pairing; re-checked because a graph published before a
    // capabilityId change would pair a flow with the wrong capability, and the cost is one compare.
    definition.capabilityId === action.capability_id
  );
};

/**
 * Can this user enter the guided flow's entry route right now?
 *
 * Mirrors `router/AuthorizationPage.tsx`, because the failure this prevents is specifically a guide
 * that starts and is then bounced by that guard — leaving a run stuck in its away state with a
 * return button that redirects every time. Each clause is one of the guard's own branches:
 *
 * - `requires_paid_plan && !hasSession` → the guard renders `NotAuthorized` (`:76`).
 * - an entry permission the payload doesn't grant → the guard fires the 402 snackbar and redirects
 *   to `permissionRedirect` (`:84-89`), via the shared `isModuleEnabled` rule.
 * - permissions not loaded yet → the guard renders nothing (`:79`). Not a bounce, but not proof of
 *   access either, so it reads as not-eligible: an offer is a promise, and this cannot keep it.
 *
 * Deliberately stricter than `isNavActionVisible`, and gated on it: "Go" is allowed to lead a user
 * to the upsell (that is the existing paywall funnel), a guided run is not. Nothing here is more
 * permissive than the plain navigation the same action already offers.
 */
export const isGuidedActionEligible = (action: IChatbotNavAction, access: GuidedAccessState): boolean => {
  if (!action?.workflow_id) return false;
  if (!isNavActionVisible(action, access.permissions)) return false;
  if (action.requires_paid_plan && !access.hasSession) return false;

  // Only when the ENTRY route has a permission gate. A route without one is not subject to the
  // guard's permission branch at all — including its fake-enterprise check, which lives inside it.
  const entryPermission = action.workflow_entry_permission;
  if (!entryPermission) return true;

  // No separate "payload not loaded yet" branch: `isModuleEnabled` already reads an absent payload
  // as not-enabled, which is the answer we want. An explicit `if (!access.permissions) return false`
  // here was measurably redundant — no test could tell the two versions apart.
  return isModuleEnabled(entryPermission, access.permissions, access.packageCode);
};
